// Batch render engine. Legacy: batchSubstituteText/batchRenderLabel/
// batchCollapseEmptyFields/batchRenderAllLabels (snapshot L7948-8163).
//
// Fabric v7 rework (map §4.2, §5.1 #2):
//   - legacy used a callback `loadFromJSON` + a `pending--`/`checkDone` counter as a
//     barrier over async barcode/QR regen. Here that becomes await loadFromJSON +
//     Promise.all over the regenerations.
//   - temp `new fabric.Canvas(el)` -> `new StaticCanvas(undefined)` (offscreen).
//   - `canvas.dispose()` is async in v7 -> await it.
//   - `fabric.util.groupSVGElements` removed -> new Group(objects).
import { StaticCanvas, Group, FabricImage, loadSVGFromString, type FabricObject } from 'fabric'
import { getCanvas, state } from './../state'
import { labelPx } from './../canvas'
import { CANVAS_CUSTOM_PROPS } from './../history'
import { generateBarcodeSVG } from './../widgets/barcode'
import { generateQRDataUrl } from './../widgets/qrcode'
import { lq, rowScope, fieldsIn } from './../liquid'
import { fitAllTexts } from './../widgets/autofit'
import type { BatchRow } from './../types'

/** Render a display string for a row via Liquid ({{field}}, filters, aggregates),
 *  then tidy orphaned separators left by empty fields (legacy batchSubstituteText). */
export function batchSubstituteText(text: string, row: BatchRow): string {
  const result = lq(text, rowScope(row))
  return result.replace(/,\s*,/g, ',').replace(/^[,\s]+|[,\s]+$/g, '').replace(/\s{2,}/g, ' ').trim()
}

/** Distinct bound field/dataset names referenced by canvas objects (recursing into groups). */
export function detectBatchFields(): string[] {
  const found = new Set<string>()
  const scan = (objs: FabricObject[]) => {
    for (const o of objs) {
      const a = o as any
      for (const s of [a.text, a.barcodeValue, a.qrValue]) {
        if (typeof s !== 'string') continue
        for (const f of fieldsIn(s)) found.add(f)
      }
      if (typeof a.getObjects === 'function') scan(a.getObjects())
    }
  }
  scan(getCanvas().getObjects())
  return [...found]
}

/** Hide post-substitution empty text objects and shift others up (legacy batchCollapseEmptyFields). */
function collapseEmptyFields(tCanvas: StaticCanvas): void {
  const texts = tCanvas.getObjects()
    .filter((o) => ['i-text', 'text', 'textbox'].includes(o.type))
    .sort((a, b) => (a.top ?? 0) - (b.top ?? 0)) as (FabricObject & Record<string, any>)[]
  if (!texts.length) return

  const empty = new Set<FabricObject>()
  for (const o of texts) {
    if ((o.text || '').replace(/[,\s\-·•|]+/g, '').trim() === '') {
      empty.add(o)
      o.set({ opacity: 0 } as any)
    }
  }
  if (!empty.size) return

  let shiftUp = 0
  for (const o of texts) {
    if (empty.has(o)) shiftUp += (o.fontSize || 14) + 3
    else if (shiftUp > 0) o.set({ top: (o.top ?? 0) - shiftUp } as any)
  }
}

async function regenBarcode(tCanvas: StaticCanvas, obj: FabricObject & Record<string, any>): Promise<void> {
  const val: string = obj.barcodeValue || ''
  if (val.includes('{{')) return // unresolved placeholder — leave as-is
  const svg = generateBarcodeSVG(obj.barcodeFormat, val, {
    font: obj.barcodeStyleFont, textPosition: obj.barcodeStyleTextPos,
    fontSize: obj.barcodeStyleFontSize, displayValue: obj.barcodeStyleShowText,
  })
  const { objects } = await loadSVGFromString(svg)
  const group = new Group(objects.filter(Boolean) as FabricObject[])
  group.set({
    left: obj.left, top: obj.top, scaleX: obj.scaleX, scaleY: obj.scaleY, angle: obj.angle,
    id: obj.id, barcodeFormat: obj.barcodeFormat, barcodeValue: val,
  } as any)
  tCanvas.remove(obj)
  tCanvas.add(group)
}

async function regenQR(tCanvas: StaticCanvas, obj: FabricObject & Record<string, any>): Promise<void> {
  const val: string = obj.qrValue || ''
  if (val.includes('{{')) return
  const dataUrl = await generateQRDataUrl(val)
  const img = await FabricImage.fromURL(dataUrl)
  img.set({ left: obj.left, top: obj.top, scaleX: obj.scaleX, scaleY: obj.scaleY, angle: obj.angle, id: obj.id, qrValue: val } as any)
  tCanvas.remove(obj)
  tCanvas.add(img)
}

/** Render one row to an offscreen PNG data URL. */
export async function batchRenderLabel(row: BatchRow, multiplier = 3): Promise<string> {
  const main = getCanvas()
  const json = main.toObject(CANVAS_CUSTOM_PROPS) // no string substitution; resolve per-object below
  const { w, h } = labelPx()
  const scope = rowScope(row)

  const tCanvas = new StaticCanvas(undefined, { width: w, height: h, backgroundColor: '#ffffff' })
  try {
    await tCanvas.loadFromJSON(json)
    // Per-object Liquid substitution (recurse groups → handles table cells too).
    const subst = (objs: any[]) => {
      for (const o of objs) {
        if (['i-text', 'text', 'textbox'].includes(o.type) && typeof o.text === 'string' && o.text.includes('{{')) o.set('text', lq(o.text, scope))
        if (typeof o.barcodeValue === 'string' && o.barcodeValue.includes('{{')) o.barcodeValue = lq(o.barcodeValue, scope)
        if (typeof o.qrValue === 'string' && o.qrValue.includes('{{')) o.qrValue = lq(o.qrValue, scope)
        if (typeof o.getObjects === 'function') subst(o.getObjects())
      }
    }
    subst(tCanvas.getObjects())
    fitAllTexts(tCanvas.getObjects()) // long {{field}} values shrink instead of wrapping (auto-fit)
    collapseEmptyFields(tCanvas)
    const objs = tCanvas.getObjects() as (FabricObject & Record<string, any>)[]
    await Promise.all([
      ...objs.filter((o) => o.barcodeFormat).map((o) => regenBarcode(tCanvas, o)),
      ...objs.filter((o) => o.qrValue != null).map((o) => regenQR(tCanvas, o)),
    ])
    tCanvas.renderAll()
    return tCanvas.toDataURL({ format: 'png', quality: 1, multiplier })
  } finally {
    await tCanvas.dispose()
  }
}

/** Render every valid row (× repeat) to PNG data URLs (legacy batchRenderAllLabels). */
export async function batchRenderAllLabels(repeat = 1): Promise<string[]> {
  const valid = state.batchData.filter((row) => Object.values(row).some((v) => v !== ''))
  const expanded: BatchRow[] = []
  for (const row of valid) for (let r = 0; r < repeat; r++) expanded.push(row)

  const images: string[] = []
  for (const row of expanded) images.push(await batchRenderLabel(row))
  return images
}
